import React from "react";
import { useTranslation } from "react-i18next";

import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import makeStyles from "@mui/styles/makeStyles";

import { MeasurementIcon } from "../../icons/MeasurementIcon";
import { MeasurementComponent, MeasurementType, SubLocation } from "../../shared/enums/Result";
import { CableNaming } from "../../shared/enums/UTP2";

import {
  getSublocationLayoutByComponentType,
  getTranslatedSubLocationName,
} from "../../shared/helpers/SubLocationHelpers";
import { MultilevelDropdown, MultilevelDropdownOption } from "../../shared/MultilevelDropdown";
import { ValidationMessage } from "../../shared/ValidationMessage";

import { measurementOptions } from "../../results/PanelMeasurementsOverview";
import { ManagedSurveyAssetType } from "../../shared/constants/managedSurveyAssetTypes";
import { SublocationSelector } from "./SublocationSelector";

interface Props {
  selectedMeasurementComponent: MeasurementComponent | undefined;
  selectedSubLocation: SubLocation | undefined;
  selectedMeasurementType?: MeasurementType;
  onChange: (measurementComponent?: MeasurementComponent, subLocation?: SubLocation) => void;
  invalid?: boolean;
  invalidMessage?: string;
  cableNaming?: CableNaming;
  assetType?: ManagedSurveyAssetType;
  disabled?: boolean;
}

const useStyles = makeStyles((theme) => ({
  dropdownContainer: {
    position: "relative",
    width: "100%",
  },
  dropdownRoot: {
    width: "100%",
  },
  dropdownItem: {
    display: "flex",
    flexWrap: "nowrap",
    alignItems: "center",
    padding: theme.spacing(1),
  },
  submenuSelectedItemIcon: {
    height: "40%",
  },
  measurementType: {
    fontSize: "1.2em",
  },
  sublocationMenuContainer: {
    padding: theme.spacing(1),
    height: "fit-content",
    width: "fit-content",
  },
  sublocationTitle: {
    paddingBottom: theme.spacing(1),
  },
  validationMessage: {
    position: "absolute",
    top: "100%",
    left: 0,
    marginTop: "5px",
    zIndex: 1,
  },
}));

export const MeasurementComponentDropdown = React.forwardRef((props: Props, ref: React.Ref<unknown>) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const switchgearMeasurementComponents = measurementOptions[props.assetType ?? "$UTP2_DEFAULT"].map(
    (option) => option.type,
  );

  const options: MultilevelDropdownOption<MeasurementComponent, SubLocation>[] = switchgearMeasurementComponents.map(
    (measurementComponent) => {
      const enumValue = MeasurementComponent[measurementComponent as keyof typeof MeasurementComponent];
      return {
        label: t(`MeasurementComponents.${measurementComponent}`),
        value: enumValue,
        hasCustomSubmenu: Boolean(getSublocationLayoutByComponentType(enumValue, props.assetType)),
      };
    },
  );

  const selectedMeasurementTypeTemplate = (
    measurementComponent: MeasurementComponent | undefined,
    selectedSubLocation: SubLocation | undefined,
  ) => {
    if (measurementComponent) {
      return (
        <span>
          <Typography variant="body1" className={classes.measurementType}>
            {t(`MeasurementComponents.${measurementComponent}`)}
          </Typography>

          {selectedSubLocation && (
            <Typography variant="subtitle2" color="textSecondary" component="span">
              {getTranslatedSubLocationName(selectedSubLocation, measurementComponent, props.cableNaming)}
            </Typography>
          )}
        </span>
      );
    } else return t("MeasurementComponents.selectMeasurementComponent");
  };

  const measurementComponentOptionTemplate = (option: MeasurementComponent) => {
    return <span className={classes.dropdownItem}>{t(`MeasurementComponents.${option}`)}</span>;
  };

  function sublocationMenuTemplate(
    measurementComponent?: MeasurementComponent,
    onClick?: (measurementComponent?: MeasurementComponent, selectedSubLocation?: SubLocation) => void,
  ) {
    const measurementComponentLayout =
      measurementComponent && getSublocationLayoutByComponentType(measurementComponent);

    return (
      measurementComponent &&
      measurementComponentLayout && (
        <Paper className={classes.sublocationMenuContainer}>
          <Typography variant="subtitle2" color="textSecondary" component="p" className={classes.sublocationTitle}>
            {`${t("SubLocations.subLocation")}:`}
          </Typography>
          <SublocationSelector
            name="subLocation"
            selectedSublocation={props.selectedSubLocation}
            selectedMeasurementComponent={props.selectedMeasurementComponent}
            measurementComponent={measurementComponent}
            onChange={onClick}
            icon={
              props.selectedMeasurementType && (
                <MeasurementIcon height="1.5em" width="1.5em" measurementType={props.selectedMeasurementType} />
              )
            }
            cableNaming={props.cableNaming}
          />
        </Paper>
      )
    );
  }

  function handleSelect(value?: MeasurementComponent, subValue?: SubLocation) {
    props.onChange(value, subValue);
  }

  return (
    <div className={classes.dropdownContainer}>
      <MultilevelDropdown
        disabled={props.disabled}
        options={options}
        onChange={handleSelect}
        selectedOption={props.selectedMeasurementComponent}
        selectedSubOption={props.selectedSubLocation}
        selectedItemTemplate={selectedMeasurementTypeTemplate}
        optionTemplate={measurementComponentOptionTemplate}
        customSubmenuTemplate={sublocationMenuTemplate}
        invalid={props.invalid}
        className={classes.dropdownRoot}
      />
      {props.invalid && props.invalidMessage && (
        <div className={classes.validationMessage}>
          <ValidationMessage invalidMessage={props.invalidMessage} validationType="error" showIcon />
        </div>
      )}
    </div>
  );
});
